import type { CombinedError } from "urql";
import type { FragmentOf, ResultOf } from "@/data-access/graphql";
import type { BriefingDefinitionFragment } from "@/data-access/scheduled-briefings/fragments/briefing-definition-fragments";
import type {
  CreateBriefingDefinition,
  DeleteBriefingDefinitionPayload,
  GetBriefingDefinition,
  PauseBriefingDefinition,
  ResumeBriefingDefinition,
  UpdateBriefingDefinition,
} from "@/data-access/scheduled-briefings/queries/briefing-definition-queries";
import {
  to12HourTime,
  to24HourTime,
} from "@/store/news-alerts/scheduleTime.helpers";
import type { BriefingCreateState } from "@/store/scheduled-briefings/briefing-create/briefing-create.slice";
import type {
  BriefingDefinition,
  BriefingDefinitionCreateInput,
  BriefingDefinitionImageAssetInput,
  BriefingDefinitionSlotInput,
  BriefingDefinitionUpdateInput,
  BriefingDefinitionValidationError,
} from "@/types/briefing-definition/briefingDefinition.types";
import type {
  BriefingDefinitionRepositoryError,
  BriefingDefinitionRepositoryResult,
} from "./briefingDefinitionRepository.types";

type BriefingDefinitionNode = FragmentOf<typeof BriefingDefinitionFragment>;
type GetBriefingDefinitionData = ResultOf<typeof GetBriefingDefinition>;
type BriefingDefinitionMutationPayload =
  | ResultOf<typeof CreateBriefingDefinition>["createBriefingDefinition"]
  | ResultOf<typeof UpdateBriefingDefinition>["updateBriefingDefinition"]
  | ResultOf<typeof PauseBriefingDefinition>["pauseBriefingDefinition"]
  | ResultOf<typeof ResumeBriefingDefinition>["resumeBriefingDefinition"];

type BriefingDefinitionOperationInfo = Extract<
  BriefingDefinitionMutationPayload | DeleteBriefingDefinitionPayload,
  { __typename: "OperationInfo" }
>;

/** Configure-step fields persisted on a briefing definition. */
export type BriefingCreateDefinitionPersistState = Pick<
  BriefingCreateState,
  | "selectedTemplateId"
  | "briefingName"
  | "overallPrompt"
  | "lookbackDays"
  | "slotInputs"
  | "slotTitles"
  | "imageAssets"
  | "scheduleFrequency"
  | "scheduleTime"
  | "timezone"
  | "validRecipientEmails"
>;

export type BriefingDefinitionBriefingCreateHydration = {
  definitionId: string;
  definitionUpdatedAt: string;
  templateUpdatedAt: string | null;
  canEdit: boolean;
  form: Pick<
    BriefingCreateState,
    | "step"
    | "selectedTemplateId"
    | "briefingName"
    | "overallPrompt"
    | "lookbackDays"
    | "slotInputs"
    | "slotTitles"
    | "imageAssets"
    | "scheduleFrequency"
    | "scheduleTime"
    | "timezone"
    | "validRecipientEmails"
    | "invalidRecipientEmails"
    | "recipientInputValue"
  >;
};

const requestFailure = (
  message: string,
): BriefingDefinitionRepositoryError => ({
  code: "REQUEST_FAILURE",
  message,
});

const invalidConfiguration = (
  message: string,
  validationErrors: BriefingDefinitionValidationError[],
): BriefingDefinitionRepositoryError => ({
  code: "INVALID_CONFIGURATION",
  message,
  validationErrors,
});

const mapDefinitionNode = (
  node: BriefingDefinitionNode,
): BriefingDefinitionRepositoryResult<BriefingDefinition> => {
  return {
    success: true,
    data: {
      id: node.id,
      name: node.name,
      description: node.description,
      overallPrompt: node.overallPrompt,
      lookbackDays: node.lookbackDays,
      recipients: [...node.recipients],
      requiresApproval: node.requiresApproval,
      canEdit: node.canEdit,
      status: node.status,
      templateId: node.template.id,
      templateUpdatedAt: node.templateUpdatedAt ?? null,
      schedule: {
        sendOn: node.schedule.sendOn,
        sendAt: node.schedule.sendAt,
        timezone: node.schedule.timezone,
      },
      slotBindings: node.slotBindings.map((binding) => ({
        slotId: binding.slotId,
        value: binding.prompt,
        title: binding.title,
        position: binding.position,
      })),
      imageAssets: node.imageAssets.map((asset) => ({
        slotId: asset.slotId,
        assetId: asset.assetId,
        url: asset.url,
      })),
      createdAt: node.createdAt,
      updatedAt: node.updatedAt,
    },
  };
};

export const mapGetBriefingDefinitionResponse = (
  data: GetBriefingDefinitionData,
): BriefingDefinitionRepositoryResult<BriefingDefinition> => {
  const node = data.briefingDefinitions.edges[0]?.node;
  if (node === undefined) {
    return {
      success: false,
      error: { code: "NOT_FOUND", message: "Briefing definition not found." },
    };
  }

  return mapDefinitionNode(node);
};

const mapGraphQLErrorCode = (
  code: unknown,
  message: string,
): BriefingDefinitionRepositoryError | null => {
  switch (code) {
    case "NOT_FOUND":
    case "NOT_OWNER":
    case "STALE_DEFINITION":
    case "TEMPLATE_CHANGED":
    case "TEMPLATE_NOT_PUBLISHED":
    case "DUPLICATE_SLOT":
    case "UNKNOWN_SLOT":
    case "MISSING_SLOT":
    case "UNKNOWN_FEED":
    case "INVALID_LOOKBACK":
    case "UNSUPPORTED_TRANSITION":
    case "INVALID_ASSET":
    case "INVALID_SLOT_TITLE":
      return { code, message };
    default:
      return null;
  }
};

const mapOperationInfo = (
  payload: BriefingDefinitionOperationInfo,
): BriefingDefinitionRepositoryError => {
  const message =
    payload.messages.map((entry) => entry.message).join(" ") ||
    "Briefing definition operation failed.";

  for (const entry of payload.messages) {
    const mapped = mapGraphQLErrorCode(entry.code, message);
    if (mapped !== null) return mapped;
  }

  const permissionMessage = payload.messages.find(
    (entry) => entry.kind === "PERMISSION",
  );
  if (permissionMessage !== undefined) {
    return { code: "NOT_OWNER", message };
  }

  const validationMessages = payload.messages.filter(
    (entry) => entry.kind === "VALIDATION",
  );
  if (validationMessages.length === 0) return requestFailure(message);

  return invalidConfiguration(
    message,
    validationMessages.map((entry) => ({
      ...(entry.field ? { path: entry.field } : {}),
      message: entry.message,
    })),
  );
};

export const mapBriefingDefinitionMutationResponse = (
  payload: BriefingDefinitionMutationPayload,
): BriefingDefinitionRepositoryResult<BriefingDefinition> => {
  if (payload.__typename === "OperationInfo") {
    return { success: false, error: mapOperationInfo(payload) };
  }

  return mapDefinitionNode(payload);
};

export const mapBriefingDefinitionDeleteMutationResponse = (
  payload: DeleteBriefingDefinitionPayload,
): BriefingDefinitionRepositoryResult<void> => {
  if (payload.__typename === "OperationInfo") {
    return { success: false, error: mapOperationInfo(payload) };
  }

  return { success: true, data: undefined };
};

export const mapBriefingDefinitionGraphQLError = (
  error: Pick<CombinedError, "graphQLErrors" | "networkError">,
): BriefingDefinitionRepositoryError => {
  for (const graphQLError of error.graphQLErrors) {
    const mapped = mapGraphQLErrorCode(
      graphQLError.extensions?.code,
      graphQLError.message,
    );
    if (mapped !== null) return mapped;
  }

  return requestFailure(
    error.networkError?.message ||
      error.graphQLErrors[0]?.message ||
      "Briefing definition request failed.",
  );
};

const invalidField = (
  path: string,
  message: string,
): BriefingDefinitionRepositoryResult<never> => ({
  success: false,
  error: invalidConfiguration(message, [{ path, message }]),
});

/**
 * Orders slots by the template's slot order; the API derives binding `position`
 * from list order. Slots with neither an input nor a title, and ids not on the
 * template, are omitted; the API saves an omitted template slot with an empty
 * prompt and no title. A sent slot without `title` also clears its saved title,
 * so every save sends each renamed title.
 */
const slotsFromState = (
  {
    slotInputs,
    slotTitles,
  }: Pick<BriefingCreateDefinitionPersistState, "slotInputs" | "slotTitles">,
  slotIds: readonly string[],
): BriefingDefinitionSlotInput[] =>
  slotIds.flatMap((slotId) => {
    const value = slotInputs[slotId];
    const title = slotTitles[slotId];
    if (value === undefined && title === undefined) return [];
    return [{ slotId, value: value ?? "", ...(title ? { title } : {}) }];
  });

/** Image asset inputs for the template's slots, in layout order. */
const imageAssetsFromState = (
  imageAssets: BriefingCreateDefinitionPersistState["imageAssets"],
  slotIds: readonly string[],
): BriefingDefinitionImageAssetInput[] =>
  slotIds.flatMap((slotId) => {
    const asset = imageAssets[slotId];
    return asset === undefined ? [] : [{ slotId, assetId: asset.assetId }];
  });

/** Slot-keyed image replacements from a definition payload. */
export const imageAssetsToState = (
  imageAssets: BriefingDefinition["imageAssets"],
): BriefingCreateDefinitionPersistState["imageAssets"] =>
  Object.fromEntries(
    imageAssets.map((asset) => [
      asset.slotId,
      { assetId: asset.assetId, url: asset.url },
    ]),
  );

/** First Frequency option. */
const DEFAULT_SEND_ON = "DAILY";
/** First Send time option (12:00 AM). */
const DEFAULT_SEND_AT = "00:00";
/** The API's own default when no timezone is sent. */
const DEFAULT_TIMEZONE = "US_EASTERN";

const mapConfigurationFields = (
  state: BriefingCreateDefinitionPersistState,
  slotIds: readonly string[],
): BriefingDefinitionRepositoryResult<
  Omit<BriefingDefinitionCreateInput, "templateId" | "templateUpdatedAt">
> => {
  const trimmedName = state.briefingName?.trim() ?? "";
  if (trimmedName === "") {
    return invalidField("name", "Enter a briefing name.");
  }

  // No schedule validation (design): empty or invalid values save as defaults.
  let sendAt: string;
  try {
    sendAt = to24HourTime(state.scheduleTime);
  } catch {
    sendAt = DEFAULT_SEND_AT;
  }

  return {
    success: true,
    data: {
      name: trimmedName,
      overallPrompt: state.overallPrompt,
      lookbackDays: state.lookbackDays,
      slots: slotsFromState(state, slotIds),
      imageAssets: imageAssetsFromState(state.imageAssets, slotIds),
      recipients: [...state.validRecipientEmails],
      sendOn: state.scheduleFrequency || DEFAULT_SEND_ON,
      sendAt,
      timezone: state.timezone || DEFAULT_TIMEZONE,
    },
  };
};

export interface BriefingCreateInputOptions {
  /** Template slot ids in layout order. */
  slotIds: readonly string[];
  /** Loaded template `updatedAt` for optimistic concurrency on create. */
  templateUpdatedAt: string;
}

/** Maps configure-step store state to {@link BriefingDefinitionCreateInput}. */
export const mapBriefingCreateStateToCreateInput = (
  state: BriefingCreateDefinitionPersistState,
  { slotIds, templateUpdatedAt }: BriefingCreateInputOptions,
): BriefingDefinitionRepositoryResult<BriefingDefinitionCreateInput> => {
  if (state.selectedTemplateId === null) {
    return invalidField("templateId", "Choose a template.");
  }

  const fields = mapConfigurationFields(state, slotIds);
  if (!fields.success) return fields;

  return {
    success: true,
    data: {
      ...fields.data,
      templateId: state.selectedTemplateId,
      templateUpdatedAt,
      requiresApproval: false,
    },
  };
};

export interface BriefingUpdateInputOptions {
  /** Template slot ids in layout order. */
  slotIds: readonly string[];
  definitionId: string;
  /** Loaded definition `updatedAt`; the API rejects stale writes. */
  expectedUpdatedAt: string;
  /** Pass to re-pin the definition to the loaded template version. */
  templateUpdatedAt?: string | null;
}

/**
 * Maps configure-step store state to {@link BriefingDefinitionUpdateInput}.
 * Leaves `requiresApproval` untouched so saving does not clear an approval requirement.
 */
export const mapBriefingCreateStateToUpdateInput = (
  state: BriefingCreateDefinitionPersistState,
  {
    slotIds,
    definitionId,
    expectedUpdatedAt,
    templateUpdatedAt,
  }: BriefingUpdateInputOptions,
): BriefingDefinitionRepositoryResult<BriefingDefinitionUpdateInput> => {
  const fields = mapConfigurationFields(state, slotIds);
  if (!fields.success) return fields;

  return {
    success: true,
    data: {
      ...fields.data,
      id: definitionId,
      expectedUpdatedAt,
      ...(templateUpdatedAt != null ? { templateUpdatedAt } : {}),
    },
  };
};

/** Maps a loaded {@link BriefingDefinition} into briefing-create store fields for edit hydration. */
export const mapBriefingDefinitionToBriefingCreateHydration = (
  definition: BriefingDefinition,
): BriefingDefinitionRepositoryResult<BriefingDefinitionBriefingCreateHydration> => {
  let scheduleTime: string;
  try {
    scheduleTime = to12HourTime(definition.schedule.sendAt);
  } catch {
    return {
      success: false,
      error: requestFailure(
        "Briefing definition returned an unsupported send time.",
      ),
    };
  }

  return {
    success: true,
    data: {
      definitionId: definition.id,
      definitionUpdatedAt: definition.updatedAt,
      templateUpdatedAt: definition.templateUpdatedAt,
      canEdit: definition.canEdit,
      form: {
        step: "configure",
        selectedTemplateId: definition.templateId,
        briefingName: definition.name,
        overallPrompt: definition.overallPrompt,
        lookbackDays: definition.lookbackDays,
        slotInputs: Object.fromEntries(
          definition.slotBindings.map((binding) => [
            binding.slotId,
            binding.value,
          ]),
        ),
        slotTitles: Object.fromEntries(
          definition.slotBindings.flatMap((binding) =>
            binding.title ? [[binding.slotId, binding.title]] : [],
          ),
        ),
        imageAssets: imageAssetsToState(definition.imageAssets),
        scheduleFrequency: definition.schedule.sendOn,
        scheduleTime,
        timezone: definition.schedule.timezone,
        validRecipientEmails: [...definition.recipients],
        invalidRecipientEmails: [],
        recipientInputValue: "",
      },
    },
  };
};
